import { useEffect } from "react"

import {
  Center,
  Container,
  Heading,
  Spinner,
  Text,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"

import { ProviderClients } from "@/client"
import ClientOverviewTab from "@/components/Client/ClientOverviewTab"
import ClientSurveysTab from "@/components/Client/ClientSurveysTab"
import ClientServicesTab from "@/components/Client/ClientServicesTab"
import ClientPurchasesTab from "@/components/Client/ClientPurchasesTab"
import { Tabs } from "@chakra-ui/react"

export const Route = createFileRoute("/_layout/client")({
  component: Client,
})

function Client() {
  const navigate = useNavigate({ from: Route.fullPath })
  const selectedClientId = sessionStorage.getItem("selectedClientId") || ""
  const selectedClientName = sessionStorage.getItem("selectedClientName") || "Client"

  const { data: clientResponse, isLoading, error } = useQuery({
    queryKey: ["client", selectedClientId],
    queryFn: () => ProviderClients.readProviderClientById({ path: { id: selectedClientId } }),
    enabled: !!selectedClientId,
  })

  const client = clientResponse || null

  useEffect(() => {
    if (!selectedClientId) {
      navigate({ to: "/clients", search: { page: 1 } })
    }
  }, [navigate, selectedClientId])

  useEffect(() => {
    if (client) {
      const preferName = client.prefername || client.firstname || ""
      const fullName = [preferName, client.lastname].filter(Boolean).join(" ") || client.providerClientId
      sessionStorage.setItem("selectedClientName", fullName)
      if (client.providerId) {
        sessionStorage.setItem("selectedClientProviderId", client.providerId)
      }
    }
  }, [client])

  if (!selectedClientId) {
    return (
      <Container maxW="full">
        <Heading size="lg" pt={12}>
          Client
        </Heading>
        <Text mt={4} color="red.500">
          No client selected.
        </Text>
      </Container>
    )
  }

  if (isLoading) {
    return (
      <Container maxW="full">
        <Heading size="lg" pt={12}>
          {selectedClientName}
        </Heading>
        <Center mt={8}>
          <Spinner size="lg" />
        </Center>
      </Container>
    )
  }

  if (error || !client) {
    return (
      <Container maxW="full">
        <Heading size="lg" pt={12}>
          {selectedClientName}
        </Heading>
        <Text mt={4} color="red.500">
          Failed to load client.
        </Text>
      </Container>
    )
  }

  const preferName = client.prefername || client.firstname || ""
  const displayName = [preferName, client.lastname].filter(Boolean).join(" ") || client.providerClientId

  return (
    <Container maxW="full">
      <Heading size="lg" pt={12}>
        {displayName}
      </Heading>
      <Text mt={1} color="gray.600">
        Client
      </Text>

      <Tabs.Root lazyMount unmountOnExit defaultValue="overview" mt={6}>
        <Tabs.List>
          <Tabs.Trigger value="overview">Overview</Tabs.Trigger>
          <Tabs.Trigger value="surveys">Surveys</Tabs.Trigger>
          <Tabs.Trigger value="services">Services</Tabs.Trigger>
          <Tabs.Trigger value="purchases">Purchases</Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="overview">
          <ClientOverviewTab client={client} providerClientId={selectedClientId} />
        </Tabs.Content>
        <Tabs.Content value="surveys">
          <ClientSurveysTab providerClientId={selectedClientId} />
        </Tabs.Content>
        <Tabs.Content value="services">
          <ClientServicesTab
            providerClientId={selectedClientId}
            providerId={client.providerId}
            pensionType={client.pensionType}
          />
        </Tabs.Content>
        <Tabs.Content value="purchases">
          <ClientPurchasesTab
            providerClientId={selectedClientId}
            providerId={client.providerId}
          />
        </Tabs.Content>
      </Tabs.Root>
    </Container>
  )
}
